import React, { useState, useEffect } from 'react';

const API = 'https://familybandhan-backend-933326390640.asia-south1.run.app';

const OCCASION_TYPES = ['Festival', 'Food', 'Annual', 'Prayer', 'Cultural', 'Other'];

const ACCENT_COLORS = {
  Festival: '#7C3AED',
  Food: '#D4A017',
  Annual: '#0F6E56',
  Prayer: '#D85A30',
  Cultural: '#185FA5',
  Other: '#888780',
};

export default function FamilyTraditions({ familyEmail }) {
  const [traditions, setTraditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', occasionType: 'Festival', description: '' });
  const [msg, setMsg] = useState('');

  useEffect(() => { loadTraditions(); }, [familyEmail]);

  async function loadTraditions() {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/traditions/${encodeURIComponent(familyEmail)}/traditions`);
      const data = await r.json();
      setTraditions(data.traditions || []);
    } catch (e) {
      setTraditions([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleAdd() {
    if (!form.name.trim()) { setMsg('Please enter a tradition name'); return; }
    setSaving(true); setMsg('');
    try {
      const r = await fetch(`${API}/api/traditions/${encodeURIComponent(familyEmail)}/traditions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error('Failed');
      setForm({ name: '', occasionType: 'Festival', description: '' });
      setShowForm(false);
      await loadTraditions();
    } catch (e) {
      setMsg('Could not save — try again');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this tradition?')) return;
    try {
      await fetch(`${API}/api/traditions/${encodeURIComponent(familyEmail)}/traditions/${id}`, { method: 'DELETE' });
      setTraditions(prev => prev.filter(t => t.id !== id));
    } catch (e) {}
  }

  if (loading) return <div style={s.loading}>Loading traditions…</div>;

  return (
    <div style={s.wrap}>
      <div style={s.topBar}>
        <div style={s.pageTitle}>Family Traditions</div>
        <button style={s.addBtn} onClick={() => { setShowForm(true); setMsg(''); }}>
          + Add Tradition
        </button>
      </div>

      {showForm && (
        <div style={s.formCard}>
          <div style={s.formTitle}>Record a tradition</div>

          <label style={s.label}>Tradition name *</label>
          <input
            style={s.input}
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="e.g. 108 Diyas on Karthika Pournami"
            maxLength={100}
          />

          <label style={s.label}>Type</label>
          <select
            style={s.input}
            value={form.occasionType}
            onChange={e => setForm(f => ({ ...f, occasionType: e.target.value }))}
          >
            {OCCASION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>

          <label style={s.label}>Description</label>
          <textarea
            style={{ ...s.input, resize: 'vertical', minHeight: 80 }}
            value={form.description}
            onChange={e => { if (e.target.value.length <= 500) setForm(f => ({ ...f, description: e.target.value })); }}
            placeholder="Describe this tradition — its origin, how it's practised, who participates…"
          />
          <div style={s.charCount}>{form.description.length} / 500</div>

          {msg && <div style={s.errMsg}>{msg}</div>}

          <div style={s.formBtns}>
            <button style={s.cancelBtn} onClick={() => { setShowForm(false); setMsg(''); }}>Cancel</button>
            <button style={s.saveBtn} onClick={handleAdd} disabled={saving}>
              {saving ? 'Saving…' : 'Save Tradition'}
            </button>
          </div>
        </div>
      )}

      {traditions.length === 0 && !showForm && (
        <div style={s.empty}>
          <div style={s.emptyIcon}>📜</div>
          <div style={s.emptyTitle}>No traditions recorded yet</div>
          <div style={s.emptyDesc}>Every family has rituals passed down through generations. Record yours before they fade.</div>
          <button style={s.addBtn} onClick={() => setShowForm(true)}>+ Record first tradition</button>
        </div>
      )}

      <div style={s.grid}>
        {traditions.map(t => {
          const accent = ACCENT_COLORS[t.occasionType] || ACCENT_COLORS.Other;
          return (
            <div key={t.id} style={s.card}>
              <div style={{ ...s.cardAccent, background: accent }} />
              <div style={s.cardBody}>
                <div style={{ ...s.occasionTag, color: accent }}>{t.occasionType}</div>
                <div style={s.tradName}>{t.name}</div>
                {t.description && <div style={s.tradDesc}>{t.description}</div>}
              </div>
              <button style={s.deleteBtn} onClick={() => handleDelete(t.id)} title="Delete">×</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const s = {
  wrap: { padding: '18px 20px', background: '#F7F4EF', minHeight: '100%' },
  loading: { padding: 60, textAlign: 'center', color: '#9CA3AF', fontSize: 14 },
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  pageTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: '#1a0533', fontWeight: 600 },
  addBtn: { background: '#D4A017', color: '#1a0533', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  formCard: { background: '#fff', border: '1px solid #E8E0D0', borderRadius: 10, padding: '18px 20px', marginBottom: 20, position: 'relative' },
  formTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 17, fontWeight: 600, color: '#1a0533', marginBottom: 14 },
  label: { fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 5, marginTop: 12 },
  input: { width: '100%', border: '1px solid #E5E7EB', borderRadius: 7, padding: '8px 10px', fontSize: 13, color: '#111827', fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' },
  charCount: { fontSize: 11, color: '#9CA3AF', textAlign: 'right', marginTop: 3 },
  errMsg: { fontSize: 12, color: '#DC2626', marginTop: 8 },
  formBtns: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 },
  cancelBtn: { background: 'none', border: '1px solid #E5E7EB', borderRadius: 6, padding: '8px 16px', fontSize: 13, color: '#6B7280', cursor: 'pointer' },
  saveBtn: { background: '#D4A017', color: '#1a0533', border: 'none', borderRadius: 6, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '50px 24px', textAlign: 'center' },
  emptyIcon: { fontSize: 40 },
  emptyTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 18, color: '#374151', fontWeight: 600 },
  emptyDesc: { fontSize: 13, color: '#9CA3AF', lineHeight: 1.6, maxWidth: 300 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 },
  card: { background: '#fff', borderRadius: 10, border: '1px solid #E8E0D0', overflow: 'hidden', position: 'relative' },
  cardAccent: { height: 4 },
  cardBody: { padding: '12px 36px 14px 14px' },
  occasionTag: { fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 },
  tradName: { fontSize: 14, fontWeight: 600, color: '#111827', fontFamily: "'Cormorant Garamond', serif", marginBottom: 5, lineHeight: 1.3 },
  tradDesc: { fontSize: 12, color: '#6B7280', lineHeight: 1.5 },
  deleteBtn: { position: 'absolute', top: 10, right: 10, background: 'none', border: 'none', color: '#D1D5DB', fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: 2 },
};